/* eslint-disable import/no-anonymous-default-export */
/* eslint-disable react/no-danger */
import { PimProductT } from '@lambda/apis/src/dynamoDb/types';
import {
  ReebeloConditions,
  REEBELO_CURRENCY,
  REEBELO_COUNTRY_CODE,
  REEBELO_DOMAINS,
} from '@lambda/reebelo';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { invert, orderBy } from 'lodash-es';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import settings from '@/settings';
import { ReviewSummaryT } from '@/settings/types';

const customRound = (num: number) => {
  const fractionalPart = num - Math.floor(num);

  if (fractionalPart < 0.25) {
    // Always round down if the fractional part is less than 0.25
    return Math.floor(num);
  }

  if (fractionalPart < 0.75) {
    // Round to the nearest half if the fractional part is between 0.25 and 0.75
    return Math.floor(num) + 0.5;
  }

  // Round up if the fractional part is 0.75 or more
  return Math.ceil(num);
};

type Product = SearchOffers;

const sortByPrice = (offers: Product[]) => orderBy(offers, (p) => p.price);

const isCheapestPsku = (
  selected: Product,
  offers: Product[],
  condition: ReebeloConditions | undefined,
) => {
  if (condition === null) return false;
  const isBrandNew = condition === ReebeloConditions.BrandNew;
  const offersByCondition = offers.filter((o) => {
    const tags = extractTags(o.tags);

    return (
      (isBrandNew && tags.condition === ReebeloConditions.BrandNew) ||
      (!isBrandNew && tags.condition !== ReebeloConditions.BrandNew)
    );
  });
  const sortedOffers = sortByPrice(offersByCondition);

  return selected.price <= sortedOffers[0].price;
};

const isCheapestPskuStorage = (
  selected: Product,
  offers: Product[],
  storage: string | undefined,
  condition: ReebeloConditions | undefined,
) => {
  if (storage == null) return false;
  const offersByStorage = offers.filter((o) => {
    const tags = extractTags(o.tags);

    return tags.storage === storage;
  });

  return isCheapestPsku(selected, offersByStorage, condition);
};

export default (props: {
  offers: Product[];
  selected: Product;
  product: PimProductT;
  handle: string;
  defaultAltTag?: string;
  outOfStock: boolean;
  vendor?: string;
  productReviewsSummary?: ReviewSummaryT;
  isOrganicAdRedirect: boolean;
}) => {
  const {
    offers,
    selected,
    product,
    handle,
    defaultAltTag,
    outOfStock,
    vendor,
    productReviewsSummary,
  } = props;

  const domain = REEBELO_DOMAINS[settings.store];
  const { color, storage, condition, psku } = extractTags(selected.tags);
  const colorCode = invert(product.colors)[color as any];
  const image = product.images[colorCode]?.[0];
  const gtin = product.gtins?.find(
    (g) => g.color === color && g.storage === storage,
  )?.gtin;
  const conditionLabel =
    condition === ReebeloConditions.BrandNew ? 'BN' : 'REF';
  const displayPskuStorageItem = isCheapestPskuStorage(
    selected,
    offers,
    storage,
    condition,
  );
  const displayPskuItem = isCheapestPsku(selected, offers, condition);
  const countryCode = REEBELO_COUNTRY_CODE[settings.store];
  const gmcIDs = [
    `GMC-${countryCode}-${psku}-${storage}-${color}-${conditionLabel}`,
    displayPskuStorageItem &&
      `Collection_${countryCode}_${psku}_${storage}-${conditionLabel}`,
    displayPskuItem && `Collection_${countryCode}_${psku}-${conditionLabel}`,
  ].filter(Boolean);

  const data = {
    '@context': 'http://schema.org/',
    '@type': 'Product',
    name: selected.title,
    url: `https://${domain}/collections/${handle}`,
    image,
    description: product.description,
    brand: { '@type': 'Brand', name: product.brand },
    gtin,
    offers: gmcIDs.map((id) => ({
      '@type': 'Offer',
      sku: id,
      itemCondition: `https://schema.org/${
        condition === ReebeloConditions.BrandNew || props.isOrganicAdRedirect
          ? 'NewCondition'
          : 'RefurbishedCondition'
      }`,
      availability: outOfStock
        ? 'https://schema.org/OutOfStock'
        : 'http://schema.org/InStock',
      price: Number(selected.price)?.toFixed(2),
      priceCurrency: REEBELO_CURRENCY[settings.store],
      gtin,
      url: `https://${domain}/collections/${handle}`,
      seller: { '@type': 'Organization', name: vendor ?? selected.vendor },
      caption: defaultAltTag,
    })),
    // Only include the review summary if there is a count or it will set default values
    ...(productReviewsSummary?.count
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: customRound(productReviewsSummary.stars),
            reviewCount: productReviewsSummary.count,
            bestRating: productReviewsSummary.maxRating,
            worstRating: productReviewsSummary.minRating,
          },
        }
      : {}),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};
